import { useMemo, useState } from 'react';
import { Tab, Tabs, Loading } from '../..';
import { useListFetch } from '../../../hooks';
import { getAllSystemPrompts } from '../../../api';
import { DraggableList } from './DraggableList';
import { promptLibraryVariables, VariablesEnum } from './const';
import type { SystemPrompt } from '../../../api';

type TabsEnum = VariablesEnum.variables | VariablesEnum.prompts;

type VariablesTabsProps = {
  isPreviewMode: boolean;
  isPromptLibraryAccess: boolean;
};

const VariablesTabs: React.FC<VariablesTabsProps> = ({
  isPreviewMode,
  isPromptLibraryAccess,
}) => {
  const [currentTab, setCurrentTab] = useState<TabsEnum>(
    VariablesEnum.variables
  );

  const { nodes, loading, intializing } = useListFetch<SystemPrompt>(
    async () => {
      const arr =
        currentTab === VariablesEnum.prompts
          ? await getAllSystemPrompts()
          : undefined;
      return { content: arr };
    },
    {
      deps: [currentTab],
    }
  );

  const promptTitles = nodes?.map(prompt => prompt.title || '') || [];
  const promptData =
    nodes?.map(prompt => ({
      id: prompt.identifier || '',
      title: prompt.title || '',
    })) || [];

  const varibleTabs = useMemo(() => {
    return isPromptLibraryAccess
      ? [
          { name: VariablesEnum.variables, label: 'Variables' },
          { name: VariablesEnum.prompts, label: 'Prompts' },
        ]
      : [{ name: VariablesEnum.variables, label: 'Variables' }];
  }, [isPromptLibraryAccess]);

  const renderPromptsContent = () => {
    if (loading || intializing) {
      return <Loading height="100px" />;
    }

    if (!promptTitles.length) {
      return (
        <div className="text-medium text-semibold text-secondary px-4">
          No prompts available
        </div>
      );
    }

    return (
      <DraggableList
        items={promptTitles}
        type={VariablesEnum.prompts}
        promptData={promptData}
      />
    );
  };

  return (
    <div
      className={`flex flex-col h-full ${
        isPreviewMode
          ? 'opacity-50 pointer-events-none select-none cursor-default bg-gray-100'
          : ''
      }`}
    >
      <Tabs<TabsEnum>
        tabs={varibleTabs}
        contentClassName="mt-md flex-1 min-h-0"
        onChange={setCurrentTab}
        className="flex flex-col h-full"
      >
        <Tab name={VariablesEnum.variables}>
          <div className="flex flex-col h-full">
            <DraggableList
              items={promptLibraryVariables}
              type={VariablesEnum.variables}
            />
          </div>
        </Tab>
        {isPromptLibraryAccess && (
          <Tab name={VariablesEnum.prompts}>
            <div className="flex flex-col h-full">{renderPromptsContent()}</div>
          </Tab>
        )}
      </Tabs>
    </div>
  );
};

export default VariablesTabs;
